

import React, {
    useState,
    useEffect,
    useRef,
} from "react";
import { useTranslation } from "react-i18next";

import api from "@helpers/api.js";
import { openProfile, profileLinkProps } from "@helpers/profile";

import Config from "@config";


import { useAuth } from "@contexts/AuthContext.js";

import * as Mui from "@mui/material";

import DownloadIcon from "@mui/icons-material/Download";
import {
    IconArrowDown,
    IconArrowRight,
    IconArrowLeft,
    IconInfoCircle,
    IconMicrophone,
    IconPhoto,
    IconX,
} from "@tabler/icons-react";

import { ReactComponent as ExcelIcon } from "@assets/custom-icons/excel.svg";
import { ReactComponent as PDFIcon } from "@assets/custom-icons/pdf.svg";

import { Button, Input, StatusChip, Text } from "@fragments";
import { MOCK_CHAT_MESSAGES, isMockChat } from '../../mockChat';

import "./styles.scss";


function extractChatId(data) {
    if (typeof data === "string" || typeof data === "number") return data;

    return data?.id ?? data?.chat_id ?? data?.chat?.id ?? data?.data?.id ?? data?.data?.chat_id;
}

function parseMessageContent(content) {
    if (typeof content !== "string") return content ?? {};

    try {
        return JSON.parse(content);
    } catch {
        return { text: content };
    }
}

function normalizeMessage(message) {
    const content = parseMessageContent(message?.content);
    const asset = message?.asset ?? content.asset ?? {};

    return {
        ...(message || {}),
        sender_id:
            message?.sender_id
            ?? message?.senderId
            ?? message?.sender?.id
            ?? message?.user_id,
        content,
        asset_type:
            message?.asset_type
            ?? message?.assetType
            ?? asset.type,
        asset_url:
            message?.asset_url
            ?? message?.assetUrl
            ?? asset.url,
        created_at:
            message?.created_at
            ?? message?.timestamps?.created
            ?? message?.created,
    };
}

function extractMessages(data) {
    const messages = Array.isArray(data)
        ? data
        : data?.messages ?? data?.data?.messages ?? data?.data ?? [];

    return Array.isArray(messages) ? messages.map(normalizeMessage) : [];
}

function getInitials(name = '') {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '';
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function groupSequentialMessages(items = []) {
    return items.reduce((groups, message) => {
        const previousGroup = groups[groups.length - 1];
        const sameSender = previousGroup
            && String(previousGroup.senderId) === String(message.sender_id);

        if (sameSender) {
            previousGroup.messages.push(message);
            return groups;
        }

        groups.push({
            senderId: message.sender_id,
            messages: [message],
        });
        return groups;
    }, []);
}

function getMessageIdentity(message = {}) {
    const id = message.id ?? message.message_id ?? message.uuid;
    if (id != null) return `id:${id}`;

    const content = parseMessageContent(message.content);
    return [
        'message',
        message.sender_id ?? message.senderId ?? message.sender?.id ?? message.user_id ?? '',
        message.created_at ?? message.timestamps?.created ?? message.created ?? '',
        content?.text ?? '',
        message.asset_url ?? message.assetUrl ?? message.asset?.url ?? '',
    ].join(':');
}

function getChatRefreshIdentity(chatInfo = {}) {
    const lastMessage = chatInfo.last_message ?? chatInfo.lastMessage ?? chatInfo.last_3?.[0];
    const messageId = lastMessage?.id ?? lastMessage?.message_id ?? lastMessage?.uuid;
    const createdAt =
        chatInfo.last_message_at
        ?? chatInfo.lastMessageAt
        ?? lastMessage?.created_at
        ?? lastMessage?.timestamps?.created
        ?? chatInfo.updated_at;

    if (messageId != null) return `message:${messageId}`;
    if (createdAt) return `created:${createdAt}`;
    if (typeof lastMessage === 'string' && lastMessage) return `content:${lastMessage}`;
    return null;
}


export default function CurrentChat({ info, handleOpenPreview, onClose }) {
    const { t } = useTranslation("chat");
    const [loading, setLoading] = useState(true);
    const [messages, setMessages] = useState([]);
    const [unreadBatch, setUnreadBatch] = useState({
        count: 0,
        firstMessageKey: null,
    });

    const { user } = useAuth();

    const [file, setFile] = useState();

    const REF_messagesBox = useRef();
    const REF_socket = useRef();
    const REF_chatId = useRef();
    const REF_newMessageInput = useRef();
    const REF_messages = useRef([]);
    const REF_firstUnreadMessage = useRef();
    const REF_conversationIdentity = useRef();
    const REF_chatRefreshIdentity = useRef();
    const REF_hasLoadedMessages = useRef(false);
    const REF_scrollToBottomAfterRender = useRef(null);
    const mockChat = isMockChat(info);

    // Chat-list polling replaces `info` with a fresh object every 30 seconds.
    // Key the conversation lifecycle to the actual chat instead, otherwise the
    // refresh clears/reloads the messages and moves a reader back to the latest
    // message while they are browsing older history.
    const conversationIdentity = mockChat
        ? `mock:${info?.chat_id ?? info?.id ?? 'chat'}`
        : `chat:${getKnownChatId(info) ?? getParticipantUserId(info) ?? info?.email ?? info?.username ?? 'unknown'}`;
    const chatRefreshIdentity = getChatRefreshIdentity(info);

    useEffect(() => {
        REF_conversationIdentity.current = conversationIdentity;
        REF_chatRefreshIdentity.current = chatRefreshIdentity;
        REF_hasLoadedMessages.current = false;
        REF_messages.current = [];
        setMessages([]);
        setUnreadBatch({ count: 0, firstMessageKey: null });
        REF_chatId.current = getKnownChatId(info);

        if (mockChat) {
            REF_scrollToBottomAfterRender.current = MOCK_CHAT_MESSAGES.length;
            replaceMessages(MOCK_CHAT_MESSAGES);
            REF_hasLoadedMessages.current = true;
            setLoading(false);
        } else {
            setLoading(true);
            handleLoadMessages();
        }

        return () => {
            if (REF_socket.current) {
                REF_socket.current.close();
                REF_socket.current = null;
            }
        };
    }, [conversationIdentity]);

    useEffect(() => {
        if (
            mockChat
            || !chatRefreshIdentity
            || REF_chatRefreshIdentity.current === chatRefreshIdentity
        ) {
            return;
        }

        REF_chatRefreshIdentity.current = chatRefreshIdentity;

        // The initial history request already contains the latest messages.
        // Subsequent identity changes are chat-list refreshes and are merged
        // without clearing the list or changing the reader's viewport.
        if (REF_hasLoadedMessages.current) {
            handleRefreshMessages();
        }
    }, [chatRefreshIdentity, conversationIdentity, mockChat]);

    useEffect(() => {
        if (REF_scrollToBottomAfterRender.current !== messages.length) return;

        REF_scrollToBottomAfterRender.current = null;
        window.requestAnimationFrame(() => scrollToBottom(false));
    }, [messages]);

    function getKnownChatId(chatInfo = info) {
        if (chatInfo?.chat_id || chatInfo?.chatId) {
            return chatInfo.chat_id ?? chatInfo.chatId;
        }

        if (
            chatInfo?.id &&
            (
                chatInfo?.user_id ||
                chatInfo?.user ||
                chatInfo?.counterpart ||
                chatInfo?.participant ||
                chatInfo?.member
            )
        ) {
            return chatInfo.id;
        }

        return null;
    }

    function getParticipantUserId(chatInfo = info) {
        return (
            chatInfo?.user_id ??
            chatInfo?.user?.id ??
            chatInfo?.counterpart?.id ??
            chatInfo?.participant?.id ??
            chatInfo?.member?.id ??
            (!getKnownChatId(chatInfo) ? chatInfo?.id : null)
        );
    }

    async function resolveChatId() {
        if (REF_chatId.current) return REF_chatId.current;

        const userId = getParticipantUserId();

        if (!userId) {
            throw new Error("Missing chat participant user id");
        }

        // Core resolves the counterpart from `receiver_id` (hashed user id); `user_id` is rejected.
        const { data } = await api.post("chat", { receiver_id: userId });
        const nextChatId = extractChatId(data);

        if (!nextChatId) {
            throw new Error("Could not resolve chat id");
        }

        REF_chatId.current = nextChatId;
        return nextChatId;
    }

    function captureViewportAnchor() {
        const container = REF_messagesBox.current;
        if (!container) return null;

        const containerRect = container.getBoundingClientRect();
        const visibleMessage = Array.from(
            container.querySelectorAll('[data-chat-message-key]'),
        ).find((element) => {
            const rect = element.getBoundingClientRect();
            return rect.bottom >= containerRect.top && rect.top <= containerRect.bottom;
        });

        if (!visibleMessage) return null;

        return {
            messageKey: visibleMessage.dataset.chatMessageKey,
            offsetTop: visibleMessage.getBoundingClientRect().top - containerRect.top,
        };
    }

    function restoreViewportAnchor(anchor) {
        if (!anchor) return;

        window.requestAnimationFrame(() => {
            const container = REF_messagesBox.current;
            if (!container) return;

            const anchoredMessage = Array.from(
                container.querySelectorAll('[data-chat-message-key]'),
            ).find((element) => element.dataset.chatMessageKey === anchor.messageKey);
            if (!anchoredMessage) return;

            const containerRect = container.getBoundingClientRect();
            const nextOffsetTop = anchoredMessage.getBoundingClientRect().top - containerRect.top;
            const offsetDelta = nextOffsetTop - anchor.offsetTop;
            if (!offsetDelta) return;

            const previousScrollBehavior = container.style.scrollBehavior;
            container.style.scrollBehavior = 'auto';
            container.scrollTop += offsetDelta;
            container.style.scrollBehavior = previousScrollBehavior;
        });
    }

    function announceIncomingMessages(incomingMessages) {
        if (!incomingMessages.length) return;

        setUnreadBatch((current) => ({
            count: current.count + incomingMessages.length,
            firstMessageKey:
                current.firstMessageKey
                ?? getMessageIdentity(incomingMessages[0]),
        }));
    }

    function replaceMessages(nextMessages, {
        announceNew = false,
        preserveViewport = false,
    } = {}) {
        const previousMessages = REF_messages.current;
        const viewportAnchor = preserveViewport ? captureViewportAnchor() : null;

        if (announceNew) {
            const existingKeys = new Set(previousMessages.map(getMessageIdentity));
            const newIncomingMessages = nextMessages.filter((message) => (
                !existingKeys.has(getMessageIdentity(message))
                && String(message.sender_id) !== String(user?.id)
            ));
            announceIncomingMessages(newIncomingMessages);
        }

        REF_messages.current = nextMessages;
        setMessages(nextMessages);
        restoreViewportAnchor(viewportAnchor);
    }

    function appendIncomingMessage(message) {
        const messageKey = getMessageIdentity(message);
        if (REF_messages.current.some((current) => getMessageIdentity(current) === messageKey)) {
            return;
        }

        const viewportAnchor = captureViewportAnchor();
        const nextMessages = [...REF_messages.current, message];
        REF_messages.current = nextMessages;
        setMessages(nextMessages);
        announceIncomingMessages([message]);
        restoreViewportAnchor(viewportAnchor);
    }

    async function handleLoadMessages(bypassReconnect = false) {
        if (mockChat) {
            replaceMessages(MOCK_CHAT_MESSAGES);
            REF_hasLoadedMessages.current = true;
            setLoading(false);
            return;
        }

        const requestConversationIdentity = conversationIdentity;

        try {
            const currentChatId = await resolveChatId();
            const { data } = await api.get(`chat/${currentChatId}/history`);
            if (REF_conversationIdentity.current !== requestConversationIdentity) return;

            if (!bypassReconnect) {
                handleConnectToChat(currentChatId);
                registerView(currentChatId);
            }

            const nextMessages = extractMessages(data).slice().reverse();
            REF_scrollToBottomAfterRender.current = nextMessages.length;
            replaceMessages(nextMessages, {
                preserveViewport: bypassReconnect,
            });
            REF_hasLoadedMessages.current = true;
        } catch (e) {
            console.log("Error loading messages", e);
        }

        setLoading(false);
    }

    async function handleRefreshMessages() {
        const requestConversationIdentity = conversationIdentity;

        try {
            const currentChatId = await resolveChatId();
            const { data } = await api.get(`chat/${currentChatId}/history`);
            if (REF_conversationIdentity.current !== requestConversationIdentity) return;

            replaceMessages(extractMessages(data).slice().reverse(), {
                announceNew: true,
                preserveViewport: true,
            });
        } catch (e) {
            console.log("Error refreshing messages", e);
        }
    }

    async function registerView(chatId) {
        try {
            const currentChatId = chatId ?? await resolveChatId();
            await api.post(`chat/${currentChatId}/registerView`);
        } catch (e) { }
    }

    function scrollToBottom(smooth = true) {
        const container = REF_messagesBox.current;
        if (!container) return;

        if (smooth) {
            container.scrollTo({
                top: container.scrollHeight,
                behavior: 'smooth',
            });
            return;
        }

        const previousScrollBehavior = container.style.scrollBehavior;
        container.style.scrollBehavior = 'auto';
        container.scrollTop = container.scrollHeight;
        container.style.scrollBehavior = previousScrollBehavior;
    }

    function handleUnreadMessagesClick() {
        const container = REF_messagesBox.current;
        const firstUnreadMessage = REF_firstUnreadMessage.current;

        if (container && firstUnreadMessage) {
            const containerRect = container.getBoundingClientRect();
            const messageRect = firstUnreadMessage.getBoundingClientRect();
            const centeredMessageTop =
                container.scrollTop
                + messageRect.top
                - containerRect.top
                - (container.clientHeight - messageRect.height) / 2;

            container.scrollTo({
                top: centeredMessageTop,
                behavior: 'smooth',
            });
        } else {
            scrollToBottom(true);
        }

        setUnreadBatch({ count: 0, firstMessageKey: null });
        registerView();
    }

    async function handleConnectToChat(chatId) {
        const endpoint = Config.wsEndpoint;
        const currentChatId = chatId ?? await resolveChatId();
        const channel = "chat." + currentChatId;

        const ws = new WebSocket(endpoint);

        ws.onopen = () => {
            if (REF_socket.current) {
                REF_socket.current.close();
            }
            REF_socket.current = ws;
            console.log("Conectado a", channel);
            ws.send(
                JSON.stringify({
                    event: "pusher:subscribe",
                    data: {
                        channel: channel,
                    },
                })
            );
        };

        ws.onmessage = (e) => {
            const data = JSON.parse(e.data);
            if (data && data.event === "MessageSent") {
                if (data.channel !== channel) return;

                const messageInfo = normalizeMessage(JSON.parse(data.data).message);

                if (String(messageInfo.sender_id) === String(user.id)) return;

                console.log("New message", data.channel, data);

                registerView(currentChatId);

                appendIncomingMessage(messageInfo);
            }
        };

        ws.onclose = () => {
            console.log("Conexão finalizada: ", channel);
            REF_socket.current = null;
        };

        ws.onerror = (e) => {
            console.log("Erro no websocket", e);
        };
    }

    const fileInputRef = useRef(null);

    const handleAddFile = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (file) {
            try {
                const maxSizeInBytes = 50 * 1024 * 1024; // 50 MB
                if (file.size > maxSizeInBytes) {
                    alert(t("upload.tooLarge"));
                    return;
                }

                const fileType = getFileType(file.type);

                if (fileType === "UNSUPPORTED") {
                    alert(t("upload.unsupported"));
                    return false;
                }

                const fileUpload = {
                    fileName: file.name,
                    type: fileType,
                };

                switch (fileType) {
                    case "IMAGE":
                        const res = await resizeImage(file, 720);
                        fileUpload.data = res.base64;
                        fileUpload.aspectRatio = res.aspectRatio || 1;
                        break;
                    case "PDF":
                    case "XLSX":
                        fileUpload.data = await convertFileToBase64(file);
                        break;
                    default:
                        console.log("Unsupported file type");
                }

                console.log(fileUpload);

                setFile({ ...fileUpload });
            } catch (e) {
                console.log("Error importing file", e);
            }
        }

        function getFileType(mimeType) {
            if (mimeType.startsWith("image/")) {
                return "IMAGE";
            } else if (mimeType === "application/pdf") {
                return "PDF";
            } else if (
                mimeType ===
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            ) {
                return "XLSX";
            } else {
                return "UNSUPPORTED";
            }
        }

        function resizeImage(file, maxWidth) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (event) => {
                    const img = new Image();
                    img.onload = () => {
                        const aspectRatio = img.width / img.height;
                        const canvas = document.createElement("canvas");
                        const ctx = canvas.getContext("2d");
                        const scaleFactor = maxWidth / img.width;
                        canvas.width = maxWidth;
                        canvas.height = img.height * scaleFactor;
                        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                        canvas.toBlob((blob) => {
                            const resizedFile = new File([blob], file.name, {
                                type: file.type,
                            });
                            convertFileToBase64(resizedFile)
                                .then((base64) => resolve({ base64, aspectRatio }))
                                .catch(reject);
                        }, file.type);
                    };
                    img.src = event.target.result;
                };
                reader.readAsDataURL(file);
            });
        }

        function convertFileToBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
        }
    };

    function ChatHeader() {
        const handleBack = () => {
            onClose();
            if (REF_socket.current) {
                REF_socket.current.close();
                REF_socket.current = null;
            }
        };

        const displayName = info.username || info.email || t("common:entities.user");
        const participantUserId = resolveParticipantUserId(info);
        const handle = info.handle ?? (info.username
            ? `@${info.username.trim().split(/\s+/)[0].toLocaleLowerCase('pt-BR')}`
            : info.email);

        return (
            <div className="chat-header">
                <Button
                    className="chat-header__back"
                    icon={<IconArrowLeft />}
                    variant="text"
                    color="inverse"
                    size="small"
                    onClick={handleBack}
                    tooltip={t("conversation.back")}
                    aria-label={t("conversation.back")}
                    preventLoading
                />

                {/* Avatar + name + handle form one link to the participant's profile. */}
                <div
                    className={`chat-header__user${participantUserId != null ? " chat-header__user--link" : ""}`}
                    {...profileLinkProps(participantUserId != null
                        ? { id: participantUserId, nickname: info.nickname, username: displayName }
                        : null)}
                >
                    <div className="user-avatar">
                        {info.avatar ? (
                            <img src={info.avatar} alt="" />
                        ) : (
                            <Text.label contrast={8}>{getInitials(displayName)}</Text.label>
                        )}
                    </div>

                    <div className="chat-header__identity">
                        <Text.h4 weight={500} contrast={9} maxLines={1}>{displayName}</Text.h4>
                        {handle && <Text.caption contrast={5} maxLines={1}>{handle}</Text.caption>}
                    </div>
                </div>

                <div className="chat-header__actions">
                    <Button
                        icon={<IconInfoCircle />}
                        variant="rounded"
                        color="overlay"
                        size="small"
                        tooltip={t("conversation.info")}
                        aria-label={t("conversation.info")}
                        preventLoading
                    />
                </div>
            </div>
        );
    }

    function MessagesBox() {
        const sequences = groupSequentialMessages(messages);

        function Loading() {
            const sequences = [
                { side: 'received', bubbles: ['long', 'medium'] },
                { side: 'sent', bubbles: ['medium'] },
                { side: 'received', bubbles: ['short', 'long', 'medium'] },
            ];

            return (
                <div
                    className="messages-loading"
                    role="status"
                    aria-label={t("conversation.loadingMessages")}
                >
                    {sequences.map(({ side, bubbles }, sequenceIndex) => (
                        <div
                            key={`${side}-${sequenceIndex}`}
                            className={`message-skeleton message-skeleton--${side}`}
                            aria-hidden="true"
                        >
                            {side === 'received' && <span className="message-skeleton__avatar" />}

                            <div className="message-skeleton__messages">
                                {bubbles.map((size, bubbleIndex) => (
                                    <span
                                        key={`${size}-${bubbleIndex}`}
                                        className={`message-skeleton__bubble message-skeleton__bubble--${size}${bubbleIndex > 0 ? ' message-skeleton__bubble--continuation' : ''}`}
                                    />
                                ))}
                            </div>

                            {side === 'sent' && <span className="message-skeleton__avatar" />}
                        </div>
                    ))}
                </div>
            );
        }

        return (
            <div
                className="messages-box"
                id="scrollableDiv"
                ref={REF_messagesBox}
            >
                {!loading ? (
                    <div className="messages-list">
                        {sequences.map((sequence, index) => (
                            <MessageSequence
                                key={sequence.messages[0]?.id ?? `sequence-${index}`}
                                messages={sequence.messages}
                                info={info}
                                currentUser={user}
                                handleOpenPreview={handleOpenPreview}
                                firstUnreadMessageKey={unreadBatch.firstMessageKey}
                                firstUnreadMessageRef={REF_firstUnreadMessage}
                            />
                        ))}
                    </div>
                ) : (
                    ["", "", "", "", "", "", "", "", ""].map((_, i) => (
                        <Loading key={i} />
                    ))
                )}
            </div>
        );
    }

    function NewMessagesNotice() {
        const label = t("conversation.newMessages", { count: unreadBatch.count });

        return (
            <div
                className="chat-new-messages"
                role="status"
                aria-live="polite"
            >
                <Button
                    className="chat-new-messages__button"
                    color="current"
                    variant="rounded"
                    size="small"
                    type="button"
                    endIcon={<IconArrowDown />}
                    onClick={handleUnreadMessagesClick}
                    aria-label={t("conversation.newMessagesAria", { label })}
                    preventLoading
                >
                    {label}
                </Button>
            </div>
        );
    }

    function Footer() {
        const [val, setVal] = useState('')
        const [loading, setLoading] = useState(false)

        async function handleSendMessage(e) {
            e?.preventDefault();

            if (!val?.trim() && !file?.data) return;

            if (mockChat) {
                const now = new Date();
                const date = [
                    now.getFullYear(),
                    String(now.getMonth() + 1).padStart(2, '0'),
                    String(now.getDate()).padStart(2, '0'),
                ].join('-');
                const time = [
                    String(now.getHours()).padStart(2, '0'),
                    String(now.getMinutes()).padStart(2, '0'),
                    String(now.getSeconds()).padStart(2, '0'),
                ].join(':');

                const message = {
                    id: `mock-message-${Date.now()}`,
                    sender_id: user?.id ?? 'mock_current_user',
                    content: {
                        text: val?.trim() || '',
                        fileName: file?.fileName,
                    },
                    asset_type: file?.type,
                    asset_url: file?.data,
                    created_at: `${date} ${time}`,
                };
                const nextMessages = [...REF_messages.current, message];
                REF_scrollToBottomAfterRender.current = nextMessages.length;
                replaceMessages(nextMessages);
                setVal('');
                setFile(null);
                return;
            }

            try {
                setLoading(true)

                const payload = {
                    text: val?.trim() || '',
                };

                if (!!file && !!file?.data) {
                    payload.asset = {
                        data: file.data,
                        type: file.type,
                        contentInfo: {
                            fileName: file.fileName,
                        },
                    };

                    payload.asset.data = payload.asset.data.split(",")[1];

                    if (file.type === "IMAGE") {
                        payload.asset.contentInfo.imageAspectRatio = file.aspectRatio;
                    }
                }

                const currentChatId = await resolveChatId();
                const { data } = await api.post(`chat/${currentChatId}/message`, payload);

                setFile(null);

                if (data?.success !== false) {
                    setVal('');
                    handleLoadMessages(true);
                }
            } catch (e) {
                console.log(e);
            } finally {
                setLoading(false)
            }
        }

        return (
            <form className="chat-footer" onSubmit={handleSendMessage}>
                <input
                    type="file"
                    ref={fileInputRef}
                    className="chat-file-input"
                    onChange={handleFileChange}
                />
                <Input.Text
                    className="chat-composer"
                    emoji
                    ref={REF_newMessageInput}
                    value={val}
                    fullWidth
                    placeholder={t("composer.placeholder")}
                    aria-label={t("composer.sendTo", {
                        name: info.username?.split(" ")[0]
                            || info.email
                            || t("composer.sendToFallback"),
                    })}
                    variant="large"
                    autoFocus
                    onInput={setVal}
                />

                <div className="chat-composer-actions">
                    <div className="chat-audio-action">
                        <StatusChip.Default
                            text={t("common:status.comingSoon")}
                            preserveCase
                            className="chat-audio-action__badge"
                        />
                        <Button
                            color="inverse"
                            variant="text"
                            size="small"
                            type="button"
                            icon={<IconMicrophone />}
                            disabled
                            aria-label={t("composer.audioComingSoon")}
                            preventLoading
                        />
                    </div>

                    <Button
                        className="chat-image-action"
                        onClick={handleAddFile}
                        color="inverse"
                        variant="text"
                        size="small"
                        type="button"
                        icon={<IconPhoto />}
                        tooltip={t("composer.attach")}
                        aria-label={t("composer.attach")}
                        preventLoading
                    />

                        <Button
                            className="chat-send-action"
                            type="submit"
                            color="primary"
                            variant="rounded"
                            size="small"
                            loading={loading}
                            icon={<IconArrowRight />}
                            tooltip={t("composer.send")}
                            aria-label={t("composer.send")}
                        />
                </div>
            </form>
        );
    }

    function FilePreview() {
        function ImagePreview() {
            return (
                <div className="file slide-bottom-fade photo">
                    <div className="file-icon-area">
                        <img
                            src={file.data}
                            style={{ aspectRatio: file.aspectRatio }}
                            className="file-icon"
                        />
                    </div>
                    <Text.caption className="file-title photo" contrast={8}>{file.fileName}</Text.caption>
                </div>
            );
        }
        function XLSXPreview() {
            return (
                <div className="file slide-bottom-fade excel">
                    <div className="file-icon-area">
                        <ExcelIcon className="file-icon excel" />
                    </div>
                    <Text.caption className="file-title excel" contrast={8}>{file.fileName}</Text.caption>
                </div>
            );
        }
        function PDFPreview() {
            return (
                <div className="file slide-bottom-fade pdf">
                    <div className="file-icon-area">
                        <PDFIcon className="file-icon pdf" />
                    </div>
                    <Text.caption className="file-title pdf" contrast={8}>{file.fileName}</Text.caption>
                </div>
            );
        }
        return (
            <div className="file-preview">
                {file.type === "IMAGE" && <ImagePreview />}
                {file.type === "XLSX" && <XLSXPreview />}
                {file.type === "PDF" && <PDFPreview />}
                <Mui.IconButton
                    type="button"
                    className="remove-attachment-btn mr-1"
                    onClick={() => setFile(null)}
                >
                    <IconX />
                </Mui.IconButton>
            </div>
        );
    }

    return (
        <div className={`current-chat${file ? ' current-chat--has-file' : ''}`}>
            <ChatHeader />
            {MessagesBox()}
            {file && <FilePreview />}
            {unreadBatch.count > 0 && <NewMessagesNotice />}
            <Footer />
        </div>
    );
}


// `userId` is resolved by the caller (chat info vs. the signed-in user carry
// their id under different keys); when present the avatar opens the profile.
function MessageAvatar({ person, userId = null }) {
    const name = person?.username || person?.name || person?.email || '';
    const avatar = person?.avatar || person?.profile_photo_url;
    const linkable = userId !== null && userId !== undefined;

    return (
        <div
            className={`message-avatar${linkable ? ' message-avatar--link' : ''}`}
            aria-hidden={linkable ? undefined : "true"}
            title={linkable ? name : undefined}
            onClick={linkable ? () => openProfile({ id: userId, nickname: person?.nickname }) : undefined}
        >
            {avatar ? (
                <img src={avatar} alt="" />
            ) : (
                <Text.caption contrast={8}>{getInitials(name)}</Text.caption>
            )}
        </div>
    );
}

// The other participant's user id, under whichever key the chat payload used.
function resolveParticipantUserId(info) {
    return (
        info?.user_id ??
        info?.user?.id ??
        info?.counterpart?.id ??
        info?.participant?.id ??
        info?.member?.id ??
        null
    );
}

function isReceivedMessage(data, info) {
    return String(data.sender_id) === String(resolveParticipantUserId(info));
}

function MessageSequence({
    messages,
    info,
    currentUser,
    handleOpenPreview,
    firstUnreadMessageKey,
    firstUnreadMessageRef,
}) {
    if (!messages.length) return null;

    const received = isReceivedMessage(messages[0], info);
    const stickyAvatar = messages.length > 1;
    const avatarClassName = [
        'message-sequence__avatar',
        stickyAvatar && 'message-sequence__avatar--sticky',
    ].filter(Boolean).join(' ');

    return (
        <div className={`message-sequence ${received ? 'received' : 'sent'}`}>
            {received && (
                <div className={avatarClassName}>
                    <MessageAvatar person={info} userId={resolveParticipantUserId(info)} />
                </div>
            )}

            <div className="message-sequence__messages">
                {messages.map((message, index) => {
                    const messageKey = getMessageIdentity(message);

                    return (
                        <Message
                            key={messageKey}
                            data={message}
                            info={info}
                            continuation={index > 0}
                            handleOpenPreview={handleOpenPreview}
                            messageKey={messageKey}
                            messageRef={
                                messageKey === firstUnreadMessageKey
                                    ? firstUnreadMessageRef
                                    : undefined
                            }
                        />
                    );
                })}
            </div>

            {!received && (
                <div className={avatarClassName}>
                    <MessageAvatar person={currentUser} userId={currentUser?.id ?? null} />
                </div>
            )}
        </div>
    );
}

function Message({ data, info, continuation, handleOpenPreview, messageKey, messageRef }) {
    const { t } = useTranslation("chat");
    const received = isReceivedMessage(data, info);

    const onlyImageMsg = data.asset_url && !data.content.text;

    const AssetComponent = {
        IMAGE: () => (
            <img
                onClick={() => handleOpenPreview(data.asset_url)}
                src={data.asset_url}
                alt={data.content?.text || t("conversation.sharedImageAlt")}
                className="message-img"
            />
        ),
        XLSX: () => (
            <div style={{ width: "100%" }} className="file slide-bottom-fade excel">
                <div className="file-icon-area">
                    <ExcelIcon className="file-icon excel" />
                </div>
                <Text.caption className="file-title excel" contrast={8}>{data.content.fileName}</Text.caption>
                <Mui.IconButton
                    className="file-download"
                    onClick={() =>
                        downloadFileFromUrl(data.asset_url, data.content.fileName)
                    }
                >
                    <DownloadIcon />
                </Mui.IconButton>
            </div>
        ),
        PDF: () => (
            <div style={{ width: "100%" }} className="file slide-bottom-fade pdf">
                <div className="file-icon-area">
                    <PDFIcon className="file-icon pdf" />
                </div>
                <Text.caption className="file-title pdf" contrast={8}>{data.content.fileName}</Text.caption>
                <Mui.IconButton
                    className="file-download"
                    onClick={() =>
                        downloadFileFromUrl(data.asset_url, data.content.fileName)
                    }
                >
                    <DownloadIcon />
                </Mui.IconButton>
            </div>
        ),
    }[data.asset_type];

    return (
        <div
            ref={messageRef}
            className={"message-row " + (received ? "received" : "sent")}
            data-chat-message-key={messageKey}
        >
            <div
                className={[
                    'chat-message',
                    received && 'received',
                    continuation && 'sequence-continuation',
                    onlyImageMsg && 'only-image',
                    data.asset_type === 'IMAGE' && 'has-image',
                ].filter(Boolean).join(' ')}
            >
                {data?.asset_url && <AssetComponent />}
                <Text.span className="msg-text" contrast={received ? 9 : 1}>
                    {data?.content?.text
                        ?.split(/(https?:\/\/[^\s]+)/g)
                        .map((part, index) => {
                            if (/(https?:\/\/[^\s]+)/g.test(part)) {
                                return (
                                    <a
                                        key={index}
                                        href={part}
                                        target="_blank"
                                        className="message-link"
                                        rel="noopener noreferrer"
                                    >
                                        {part}
                                    </a>
                                );
                            }
                            return part;
                        })}
                </Text.span>
                <Text.caption className="date-label" contrast={received ? 6 : 2}>
                    {data?.created_at?.split(" ")?.[1]?.slice(0, 5) || ''}
                </Text.caption>
            </div>
        </div>
    );
}



async function downloadFileFromUrl(url, filename) {
    try {
        var link = document.createElement("a");
        link.download = filename;
        link.href = url;
        link.click();
        link.remove();
    } catch (error) {
        console.error("There was an error downloading the file:", error);
    }
}
